import React, { useEffect, useState, useRef } from "react";
import { Loader2, FileSearch, FileUp, Sparkles, AlertCircle, CheckCircle2, XCircle, AlertTriangle, Copy, Download, ArrowRight } from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader from "../components/app/PageHeader";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";

const RULESETS = [
  { value: "all", label: "All rulesets (FCA + ASA + ICO)" },
  { value: "fca", label: "FCA Financial Promotions" },
  { value: "asa", label: "ASA Advertising Standards" },
  { value: "ico", label: "ICO Communications / UK GDPR" },
];

function ScoreCircle({ score, verdict }) {
  const [drawn, setDrawn] = useState(0);
  useEffect(() => { const t = setTimeout(() => setDrawn(score), 80); return () => clearTimeout(t); }, [score]);
  const radius = 50; const circ = 2 * Math.PI * radius;
  const offset = circ - (drawn / 100) * circ;
  const color = verdict === "pass" ? "#10B981" : verdict === "review" ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative" style={{ width: 132, height: 132 }}>
      <svg viewBox="0 0 132 132" width="132" height="132">
        <circle cx="66" cy="66" r={radius} fill="none" strokeWidth="10" stroke="#E2E8F0" />
        <circle cx="66" cy="66" r={radius} fill="none" strokeWidth="10" stroke={color} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} transform="rotate(-90 66 66)" className="score-arc" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="t-mono" style={{ fontSize: 32, fontWeight: 700 }}>{Math.round(drawn)}</span>
        <span className="t-caption">/ 100</span>
      </div>
    </div>
  );
}

function FlaggedCard({ item, index, scanId, onOverridden }) {
  const [showForm, setShowForm] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 20) { alert("Reason must be at least 20 characters."); return; }
    setSaving(true);
    try {
      await api.post("/api/compliance/override", { scan_id: scanId, flag_index: index, reason });
      onOverridden(index, reason);
      setShowForm(false); setReason("");
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg p-4 border" style={{ background: item.overridden ? "rgba(148,163,184,0.06)" : item.severity === "critical" ? "rgba(239,68,68,0.04)" : item.severity === "warning" ? "rgba(245,158,11,0.04)" : "rgba(79,70,229,0.04)", borderColor: "#E2E8F0", borderLeftWidth: 3, borderLeftColor: item.overridden ? "#94A3B8" : item.severity === "critical" ? "#EF4444" : item.severity === "warning" ? "#F59E0B" : "#4F46E5" }} data-testid={`flag-${index}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className={`badge2 ${item.severity === "critical" ? "badge-failed" : item.severity === "warning" ? "badge-pending" : "badge-locked"}`}>{item.severity}</span>
        <span className="t-mono t-caption">{item.rule_reference}</span>
        {item.overridden && <span className="badge2 badge-locked" data-testid={`flag-overridden-${index}`}>Overridden</span>}
      </div>
      <p className={`italic t-body-sm mb-2 ${item.overridden ? "line-through" : ""}`} style={{ color: "#475569" }}>"{item.excerpt}"</p>
      <p className="t-body-sm mb-2">{item.explanation}</p>
      <p className="t-body-sm" style={{ color: "#065F46" }}><strong>Fix:</strong> {item.suggested_fix}</p>
      {item.overridden && (
        <div className="mt-3 p-3 bg-surface rounded-md t-body-sm border border-border2">
          <div className="t-caption mb-1">Overridden by <strong>{item.override_by}</strong> · {item.override_at ? new Date(item.override_at).toLocaleString() : ""}</div>
          <div>"{item.override_reason}"</div>
        </div>
      )}
      {!item.overridden && scanId && (
        showForm ? (
          <div className="mt-3">
            <textarea className="textarea2" rows={3} placeholder="Why are you overriding this flag? (minimum 20 characters — this is logged to the audit trail)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`override-reason-${index}`} />
            <div className="flex justify-between items-center mt-2">
              <span className="t-caption">{reason.length}/20 minimum</span>
              <div className="flex gap-2">
                <button onClick={() => { setShowForm(false); setReason(""); }} className="btn btn-secondary btn-sm">Cancel</button>
                <button onClick={submit} disabled={saving || reason.trim().length < 20} className="btn btn-primary btn-sm" data-testid={`override-submit-${index}`}>{saving ? <Loader2 size={12} className="animate-spin" /> : null}Submit override</button>
              </div>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowForm(true)} className="btn btn-ghost btn-sm mt-3" data-testid={`override-${index}`}>Override with written reason</button>
        )
      )}
    </div>
  );
}

export default function ComplianceChecker() {
  const [mode, setMode] = useState("quick"); // quick | document | history
  const [text, setText] = useState("");
  const [ruleset, setRuleset] = useState("all");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [docContent, setDocContent] = useState("");
  const fileRef = useRef();

  useEffect(() => { if (mode === "history") loadHistory(); }, [mode]);

  const loadHistory = async () => {
    try {
      const [h, a] = await Promise.all([api.get("/api/compliance/history"), api.get("/api/compliance/analytics?days=30")]);
      setHistory(h.data); setAnalytics(a.data);
    } catch {}
  };

  const runCheck = async (textToScan) => {
    setError(""); setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/api/compliance/scan", { text: textToScan, ruleset });
      track(EVT.compliance_check_run, { ruleset, mode: "text", verdict: data.verdict, score: data.score });
      setResult(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleOverridden = (idx, reason) => {
    setResult((r) => {
      if (!r) return r;
      const next = { ...r, flagged_items: r.flagged_items.map((it, i) => i === idx ? { ...it, overridden: true, override_reason: reason, override_at: new Date().toISOString() } : it) };
      return next;
    });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 25 * 1024 * 1024) { setError("Max 25MB"); return; }
    setError(""); setLoading(true); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("ruleset", ruleset);
      const { data } = await api.post("/api/compliance/scan-file", form, { headers: { "Content-Type": "multipart/form-data" } });
      track(EVT.compliance_check_run, { ruleset, mode: "file", verdict: data.verdict, score: data.score });
      // Normalise shape to match /api/compliance/scan response
      setResult({
        id: data.id, score: data.score, verdict: data.verdict, summary: data.summary,
        flagged_items: data.flagged || data.flagged_items || [], passed_checks: data.passed_checks || [],
        char_count: data.char_count, word_count: data.word_count, filename: data.filename,
      });
      setDocContent(`${data.filename || file.name} · ${data.char_count} chars · ${data.word_count} words`);
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  return (
    <AppShell>
      <PageHeader title="Compliance checker" subtitle="Scan any marketing copy or client communication against FCA, ASA, and ICO rules." />

      <div className="card2 mb-6 inline-flex p-1 rounded-md bg-surface" data-testid="checker-tabs">
        {[
          { id: "quick", label: "Quick check", icon: Sparkles },
          { id: "document", label: "Document upload", icon: FileUp },
          { id: "history", label: "History & analytics", icon: FileSearch },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => { setMode(t.id); setResult(null); }}
            className={`px-4 py-2 rounded-md t-body-sm font-medium flex items-center gap-2 ${mode === t.id ? "bg-white shadow-card text-ink" : "text-slate2"}`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {(mode === "quick" || mode === "document") && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-6">
          <div className="card2 p-6">
            {mode === "quick" ? (
              <>
                <label className="label2">Paste your text</label>
                <textarea className="textarea2 mb-3" rows={12} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste marketing copy, client letter, financial promotion…" data-testid="quick-textarea" />
                <div className="flex justify-between t-caption mb-4"><span>Up to 18,000 chars</span><span>{text.length}</span></div>
              </>
            ) : (
              <>
                <label className="label2">Upload document</label>
                <label className="block border-2 border-dashed border-border2 rounded-lg p-10 text-center cursor-pointer hover:border-indigo2">
                  <FileUp size={28} className="mx-auto mb-2 text-slate2" />
                  <div className="t-body-sm">Drop file or click to browse</div>
                  <div className="t-caption mt-1">PDF, DOCX, TXT · max 25MB</div>
                  <input type="file" ref={fileRef} className="hidden" accept=".pdf,.docx,.doc,.txt" onChange={onFile} data-testid="doc-upload" />
                </label>
                {docContent && <div className="mt-3 p-3 bg-surface rounded-md text-xs t-mono max-h-28 overflow-y-auto">{docContent.slice(0, 600)}…</div>}
              </>
            )}

            <label className="label2 mt-4">Check against</label>
            <select className="input2 mb-4" value={ruleset} onChange={(e) => setRuleset(e.target.value)} data-testid="checker-ruleset">
              {RULESETS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>

            {mode === "quick" && (
              <button className="btn btn-primary btn-block" onClick={() => runCheck(text)} disabled={loading || !text.trim()} data-testid="run-check">
                {loading ? <><Loader2 size={14} className="animate-spin" />Scanning…</> : <>Run compliance check <ArrowRight size={14} /></>}
              </button>
            )}
            {error && <div className="mt-3 err2"><AlertCircle size={14} className="inline mr-1" />{error}</div>}
          </div>

          <div>
            {loading && (
              <div className="card2 p-12 text-center">
                <Loader2 size={36} className="animate-spin text-indigo2 mx-auto mb-4" />
                <p className="t-body text-slate2">Compliche is analysing your text against FCA, ASA, and ICO rulesets…</p>
              </div>
            )}
            {!loading && result && (
              <div className="card2 p-6 animate-slide-up" data-testid="checker-result">
                <div className="flex items-center gap-5 mb-5">
                  <ScoreCircle score={result.score} verdict={result.verdict} />
                  <div>
                    {result.verdict === "pass" && <span className="badge2 badge-verified"><CheckCircle2 size={12} />PASS</span>}
                    {result.verdict === "review" && <span className="badge2 badge-pending"><AlertTriangle size={12} />REVIEW REQUIRED</span>}
                    {result.verdict === "fail" && <span className="badge2 badge-failed"><XCircle size={12} />FAIL</span>}
                    <p className="t-body mt-2">{result.summary}</p>
                    <div className="t-caption mt-2 t-mono">{result.id}</div>
                  </div>
                </div>

                {result.flagged_items.length > 0 && (
                  <>
                    <div className="t-label text-slate2 mb-3">Flagged items ({result.flagged_items.length})</div>
                    <div className="space-y-3 mb-4">
                      {result.flagged_items.map((it, i) => <FlaggedCard key={i} item={it} index={i} scanId={result.id} onOverridden={handleOverridden} />)}
                    </div>
                  </>
                )}
                {result.passed_checks?.length > 0 && (
                  <details className="mb-4">
                    <summary className="t-body-sm cursor-pointer text-emerald2-dark font-medium">✓ {result.passed_checks.length} passed checks</summary>
                    <ul className="mt-2 space-y-1">{result.passed_checks.map((c, i) => <li key={i} className="t-body-sm pl-4">• {c}</li>)}</ul>
                  </details>
                )}
                <div className="flex gap-2 flex-wrap">
                  <button className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard.writeText(text)}><Copy size={14} />Copy text</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => window.print()}><Download size={14} />Print report</button>
                </div>
              </div>
            )}
            {!loading && !result && <div className="card2 p-12 text-center"><Sparkles size={32} className="text-indigo2 mx-auto mb-3" /><p className="t-body text-slate2">Results will appear here after you run a check.</p></div>}
          </div>
        </div>
      )}

      {mode === "history" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="card2 p-5"><div className="t-label text-slate2">Avg score (30d)</div><div className="t-mono" style={{ fontSize: 30, fontWeight: 700 }}>{analytics?.avg_score ?? "—"}</div></div>
            <div className="card2 p-5"><div className="t-label text-slate2">Total checks</div><div className="t-mono" style={{ fontSize: 30, fontWeight: 700 }}>{analytics?.total ?? 0}</div></div>
            <div className="card2 p-5"><div className="t-label text-slate2">Pass</div><div className="t-h2 text-emerald2-dark">{analytics?.verdict_breakdown?.pass ?? 0}</div></div>
            <div className="card2 p-5"><div className="t-label text-slate2">Fail</div><div className="t-h2 text-rose2">{analytics?.verdict_breakdown?.fail ?? 0}</div></div>
          </div>

          {analytics?.top_violations?.length > 0 && (
            <div className="card2 p-5 mb-6">
              <h3 className="t-h3 mb-4">Top violations (30 days)</h3>
              <div className="space-y-2">
                {analytics.top_violations.map((v, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="t-mono t-caption w-6">{i + 1}.</span>
                    <span className="flex-1 t-body-sm">{v.rule}</span>
                    <span className="t-mono t-caption">{v.count}×</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card2 overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface border-b border-border2">
                <tr className="text-left">
                  <th className="px-5 py-3 t-label text-slate2">When</th>
                  <th className="px-5 py-3 t-label text-slate2">Run by</th>
                  <th className="px-5 py-3 t-label text-slate2">Excerpt</th>
                  <th className="px-5 py-3 t-label text-slate2">Score</th>
                  <th className="px-5 py-3 t-label text-slate2">Verdict</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border2">
                {history.map((c) => (
                  <tr key={c.id} className="hover:bg-surface">
                    <td className="px-5 py-4 t-caption">{new Date(c.created_at).toLocaleString()}</td>
                    <td className="px-5 py-4 t-body-sm">{c.run_by_name || "—"}</td>
                    <td className="px-5 py-4 t-body-sm max-w-md truncate">{c.text_excerpt}</td>
                    <td className="px-5 py-4 t-mono">{c.score}</td>
                    <td className="px-5 py-4">
                      <span className={`badge2 ${c.verdict === "pass" ? "badge-verified" : c.verdict === "review" ? "badge-pending" : "badge-failed"}`}>{c.verdict}</span>
                    </td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center t-body text-slate2">No checks run yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AppShell>
  );
}
